<template>
    <div class="content">
        <div class="container-fluid">
            <!-- Titre & breadcrumb -->
            <div class="page-heading row justify-content-between align-items-center py-3">
                <div class="col-lg-4">
                    <h4 class="page-title">{{ $t('componentEditAjax.title') }}</h4>
                </div>
                <div class="col-auto order-2 d-none d-sm-block">
                    <ol class="breadcrumb m-0 py-2">
                        <li class="breadcrumb-item">
                            <router-link to="/installation-list">{{ $t('common.installerSpace') }}</router-link>
                        </li>
                        <li class="breadcrumb-item">
                            <router-link :to="pathToInstallationEditComponents">{{ $t('componentEditAjax.breadcrumb') }}</router-link>
                        </li>
                        <li class="breadcrumb-item active">{{ $t('componentEditAjax.title') }}</li>
                    </ol>
                </div>
            </div>

            <!-- Formulaire -->
            <form ref="formRef" id="ajaxForm" name="ajaxForm" @submit.prevent="handleSubmit" class="pb-4">
                <div class="row">
                    <div class="col-xxl-8 col-xl-10">
                        <div class="card">
                            <div class="card-header pb-0">
                                <h4 class="header-title">{{ $t('componentEditAjax.accountSection') }}</h4>
                            </div>
                            <div class="card-body">
                                <div class="row">
                                    <div class="mb-2 col-md-6">
                                        <label for="ajax-user" class="form-label">{{ $t('common.email') }}</label>
                                        <input type="email" id="ajax-user" class="form-control" v-model="form.user"
                                            :placeholder="$t('componentEditAjax.emailPlaceholder')"
                                            pattern="[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}"
                                            maxlength="254" autocomplete="off" required />
                                    </div>
                                    <div class="mb-2 col-md-6">
                                        <PasswordComponent id="ajax-password" v-model="form.password" tabindex="0" />
                                    </div>
                                    <div v-for="field in keyFields" :key="field.name" class="mb-2 col-md-6">
                                        <label :for="`ajax-${field.name}`" class="form-label">{{ field.label }}</label>
                                        <div class="input-group">
                                            <input type="text" :id="`ajax-${field.name}`" class="form-control" v-model="form[field.name]"
                                                :placeholder="$t(field.placeholder)" maxlength="64" autocomplete="off" required />
                                            <button v-if="canPaste" type="button" class="btn btn-light border"
                                                :title="$t('common.paste')" :aria-label="$t('common.paste')" @click="paste(field.name)">
                                                <i class="mdi mdi-content-paste"></i>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                                <p class="text-info fw-bold font-12 mt-2 mb-0">
                                    <i class="mdi mdi-information-outline me-1"></i>{{ $t('componentEditAjax.notStoredInfo') }}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
                <!-- Form actions -->
                <div class="form-actions-sticky d-flex justify-content-center align-items-center">
                    <router-link :to="pathToInstallationEditComponents" class="btn btn-outline-secondary me-2">{{ $t('common.back') }}</router-link>
                    <button type="submit" class="btn btn-primary">{{ $t('common.save') }}</button>
                </div>
            </form>
        </div>
    </div>
</template>

<script>
import { computed, nextTick, reactive, ref } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useUserStore } from '@/stores/userStore';
import { useI18n } from 'vue-i18n';
import { showToast } from '@/services/toast';
import PasswordComponent from '@/components/password.vue';

export default {
    name: 'ComponentEditAjax',
    components: {
        PasswordComponent,
    },
    setup() {
        const userStore = useUserStore();
        const router = useRouter();
        const route = useRoute();
        const { t } = useI18n();
        const facilityId = parseInt(route.query.facilityId);
        const hubId = parseInt(route.query.hubId);
        const hub = userStore.getHub(facilityId, hubId);
        const formRef = ref(null);

        // Always empty: the cloud does not send the AJAX account back
        const form = reactive({
            user: '',
            password: '',
            api_key: '',
            aws_id: '',
            aws_secret: '',
            aws_queue_name: '',
        });

        // Technical labels are not translated in the app either
        const keyFields = [
            { name: 'api_key', label: 'X-Api-Key', placeholder: 'componentEditAjax.apiKeyPlaceholder' },
            { name: 'aws_id', label: 'AWS access key ID', placeholder: 'componentEditAjax.awsIdPlaceholder' },
            { name: 'aws_secret', label: 'AWS secret access key', placeholder: 'componentEditAjax.awsSecretPlaceholder' },
            { name: 'aws_queue_name', label: 'Events queue', placeholder: 'componentEditAjax.queueNamePlaceholder' },
        ];

        // The clipboard API only exists on secure origins
        const canPaste = Boolean(navigator.clipboard && navigator.clipboard.readText);

        const paste = async (field) => {
            try {
                const text = await navigator.clipboard.readText();
                if (text) form[field] = text;
            } catch (error) {
                console.error('Clipboard reading failed', error);
            }
        };

        const pathToInstallationEditComponents = computed(() => {
            return `/installation-edit?id=${facilityId}&action=components`;
        });

        const handleSubmit = async () => {
            // As in the app, fields are trimmed before validation, except the password
            for (const field of ['user', 'api_key', 'aws_id', 'aws_secret', 'aws_queue_name']) {
                form[field] = form[field].trim();
            }
            await nextTick();
            if (!formRef.value.reportValidity()) return;

            try {
                await userStore.configureAjax(facilityId, hub, { ...form });
                await router.push(pathToInstallationEditComponents.value);
                showToast('success', t('common.updateSuccess'));
            } catch (error) {
                showToast('error', error);
            }
        };

        return {
            formRef,
            form,
            keyFields,
            canPaste,
            paste,
            pathToInstallationEditComponents,
            handleSubmit,
        };
    },
};
</script>
